import {
    Component,
    ViewEncapsulation,
    ChangeDetectionStrategy,
    Input,
    ElementRef,
    ViewChild,
    Output,
    EventEmitter,
    ChangeDetectorRef,
    AfterViewInit,
    Renderer2,
} from '@angular/core';
import * as AdaptiveHtml from 'adaptive-html';
import * as AdaptiveCards from 'adaptivecards';
import { marked } from 'marked';

import { adaptiveCardConfig } from './adaptive-card.constants';
import { AdaptiveCardSource } from '../../interfaces/adaptive-card-source.interface';

@Component({
    selector: 'mapp-adaptive-card',
    templateUrl: './adaptive-card.component.html',
    styleUrls: ['./adaptive-card.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class AdaptiveCardComponent implements AfterViewInit {
    @Input() result: string;

    @Input() createAdaptiveCard = true;

    @Input() card: string | AdaptiveCardSource;

    @Output() typingFinished = new EventEmitter<void>();

    @Output() actionCalled = new EventEmitter<string>();

    @ViewChild('containerRef', { static: false }) containerRef: ElementRef;

    /**
     * Constructs an instance of the AdaptiveCardComponent.
     * @param ref - An instance of `ChangeDetectorRef` used to detect and respond to changes in the component's state.
     * @param renderer - An instance of `Renderer2` used for safely manipulating the DOM.
     */
    constructor(
        private ref: ChangeDetectorRef,
        private renderer: Renderer2
    ) {}

    /**
     *
     */
    ngAfterViewInit(): void {
        AdaptiveCards.AdaptiveCard.onProcessMarkdown = (text, result) => {
            result.outputHtml = marked.parseInline(text); // ✅ safe call
            result.didProcess = true;
        };

        this.checkAndCreateCard();
        // eslint-disable-next-line vars-on-top, no-var, @typescript-eslint/no-this-alias
        var self = this;
        const adaptiveCard = new AdaptiveCards.AdaptiveCard();
        adaptiveCard.hostConfig = new AdaptiveCards.HostConfig(adaptiveCardConfig);

        adaptiveCard.onExecuteAction = function (action: any) {
            const { url } = action;
            const data: any = (action as any).data || {};
            if (action._propertyBag?.type === 'Action.OpenUrl') {
                window.open(url, '_blank');
            } else if (
                action._propertyBag?.type === 'Action.Execute' &&
                action._propertyBag?.title
            ) {
                self.actionCalled.emit(action._propertyBag?.title);
            } else if (
                action._propertyBag?.type === 'Action.Submit' &&
                action._propertyBag?.data?.typeAction === 'feedback'
            ) {
                const d: any = (action as any).data || {};
                const { buttonId, action: vote, sourceKey, sourceId } = d;

                const {
                    action: actionFeedback,
                    url: sourceLink,
                    sourceType,
                } = action._propertyBag.data;

                const pressedEl = document.getElementById(buttonId);
                const container = pressedEl?.closest<HTMLElement>(
                    `#external-source-container-${sourceId}`
                );
                const likeBtn = container?.querySelector<HTMLElement>(
                    'img[aria-label="Like"], img[title="Like"]'
                );
                const dislikeBtn = container?.querySelector<HTMLElement>(
                    'img[aria-label="Dislike"], img[title="Dislike"]'
                );

                let currentElement: HTMLElement | null;
                let oppositeElement: HTMLElement | null;

                if (data.action === 'like') {
                    currentElement = likeBtn || null;
                    oppositeElement = dislikeBtn || null;
                } else {
                    currentElement = dislikeBtn || null;
                    oppositeElement = likeBtn || null;
                }

                if (currentElement && currentElement.tagName !== 'IMG') {
                    const imgBtn = currentElement.querySelector<HTMLElement>(
                        'img[role="button"], .ac-image.ac-selectable[role="button"]'
                    );
                    if (imgBtn) {
                        currentElement = imgBtn;
                    }
                }

                if (!currentElement) {
                    return;
                }

                currentElement.setAttribute('aria-pressed', 'true');
                currentElement.setAttribute('aria-disabled', 'true');
                currentElement.classList.add('chat-action-external-sources');
                currentElement.classList.add('chat-action-external-sources--selected');
                currentElement.classList.add('chat-action-external-sources--disabled');
                oppositeElement.classList.add('chat-action-external-sources');
                oppositeElement.classList.add('chat-action-external-sources--disabled');
                oppositeElement.setAttribute('aria-pressed', 'false');
                oppositeElement.setAttribute('aria-disabled', 'true');

                self.sendFeedback(actionFeedback, sourceLink, sourceType);
            }
        };
        adaptiveCard.parse(this.card);

        if (this.containerRef) {
            const renderedCard = adaptiveCard.render();
            const internetSearchButtons = renderedCard.querySelectorAll('#internet-search-card');
            if (internetSearchButtons) {
                const button = renderedCard.querySelector('button[title="Cancel"]');
                if (button) {
                    this.renderer.setStyle(button, 'background-color', '#fff');
                    this.renderer.setStyle(button, 'color', '#0b41ad');
                    this.renderer.setStyle(button, 'border', '1px solid #bbd2ff');
                }
            }

            const resultElements = renderedCard.querySelectorAll('#table');

            resultElements.forEach((el, index) => {
                const styledTables = this.generateStyledTables(this.result);
                if (styledTables[index]) {
                    el.innerHTML = styledTables[index];
                }
            });

            const element = this.containerRef.nativeElement;
            element.appendChild(renderedCard);
            this.typingFinished.emit();
            this.ref.detectChanges();
        }
    }

    /**
     *
     * @param type
     * @param sourceLink
     * @param fromInternetSearch
     * @param sourceType
     */
    sendFeedback(type: 'like' | 'dislike', sourceLink: string, sourceType: string) {
        const { pendo } = window as any;
        if (pendo && pendo.track) {
            pendo.track('Data source feedback', {
                link: sourceLink,
                feedback: type,
                sourceType,
            });
        }
    }

    /**
     *
     */
    private checkCardFormat(): boolean {
        return !!(
            this.card &&
            typeof this.card === 'object' &&
            this.card.body &&
            this.card.body[0]?.items &&
            this.card.body[0].items[0]?.columns &&
            this.card.body[0].items[0].columns[0] &&
            this.card.body[0].items[0].columns[0].items
        );
    }

    /**
     *
     */
    private checkAndCreateCard() {
        if (!this.card) {
            this.card = {
                type: 'Container',
                body: [{ items: [] }],
            };
        }

        if (this.card && typeof this.card === 'string') {
            this.card = JSON.parse(this.card);
        }

        if (typeof this.card === 'object' && !this.checkCardFormat()) {
            this.card.body[0].type = 'Container';
            this.card.body[0].items = [
                {
                    type: 'ColumnSet',
                    columns: [
                        {
                            type: 'Column',
                            items: [],
                        },
                    ],
                },
            ];
        }

        const cardAsObject = this.card as AdaptiveCardSource;
        const textForCard = cardAsObject.body?.[0]?.text || this.result;

        // if(this.containsUrlEncoding(textForCard)){
        //     const decodedText = encodeURI(textForCard);
        //     (this.card as AdaptiveCardSource).body[0].items[0].columns[0].items =
        //         this.createResultItemsforCard(decodedText);
        //     return;
        // }

        if (this.containsTable(textForCard)) {
            const tables = this.transformHtmlToAdaptiveCard(textForCard);
            (this.card as AdaptiveCardSource).body[0].items[0].columns[0].items = tables;
        } else {
            (this.card as AdaptiveCardSource).body[0].items[0].columns[0].items =
                this.createResultItemsforCard(textForCard);
        }
    }

    /**
     *
     * @param htmlString
     */
    private createResultItemsforCard(htmlString = '') {
        const adaptiveCardJson = AdaptiveHtml.toJSON(htmlString, {
            version: '1.6',
        });

        return adaptiveCardJson.body;
    }

    /**
     *
     * @param html
     */
    private containsTable(html) {
        return /<table[\s\S]*?>[\s\S]*?<\/table>/i.test(html);
    }

    /**
     *
     * @param html
     */
    private containsUrlEncoding(html: string): boolean {
        const urlEncodedPattern = /%[0-9A-Fa-f]{2}/;
        return urlEncodedPattern.test(html);
    }

    /**
     *
     * @param htmlString
     */
    transformHtmlToAdaptiveCard(htmlString: string): any {
        const card: any = {
            type: 'AdaptiveCard',
            version: '1.6',
            body: [],
        };

        // Convert  string HTML to DOM Object.
        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlString, 'text/html');

        // get content and transform to elements for Adaptive Card
        this.processHtmlElements(doc.body, card.body);
        return card.body;
    }

    /**
     *
     * @param element
     * @param body
     */
    private processHtmlElements(element: HTMLElement, body: any[]): void {
        for (const child of Array.from(element.children)) {
            switch (child.tagName.toLowerCase()) {
                case 'h1':
                case 'h2':
                case 'h3':
                case 'h4':
                case 'h5':
                case 'h6':
                    body.push(this.transformHeader(child));
                    break;
                case 'p':
                    body.push(this.transformText(child));
                    break;
                case 'ul':
                case 'ol':
                    body.push(...this.transformList(child as HTMLElement));
                    break;
                case 'table':
                    body.push({
                        type: 'ColumnSet',
                        id: 'table',
                        columns: [
                            {
                                type: 'Column',
                                items: [],
                            },
                        ],
                    });
                    break;
                default:
                    break;
            }
        }
    }

    /**
     *
     * @param element
     */
    private transformHeader(element: any): any {
        return {
            type: 'TextBlock',
            text: element.innerText,
            weight: this.mapHeaderWeight(element.tagName.toLowerCase()),
            size: this.mapHeaderSize(element.tagName.toLowerCase()),
        };
    }

    /**
     *
     * @param tag
     */
    private mapHeaderWeight(tag: string): string {
        switch (tag) {
            case 'h1':
                return 'Bolder';
            case 'h2':
                return 'Bolder';
            default:
                return 'Default';
        }
    }

    /**
     *
     * @param tag
     */
    private mapHeaderSize(tag: string): string {
        switch (tag) {
            case 'h1':
                return 'Large';
            case 'h2':
                return 'Medium';
            case 'h3':
                return 'Medium';
            case 'h4':
                return 'Small';
            default:
                return 'Default';
        }
    }

    /**
     *
     * @param element
     * @param level
     */
    transformList(element: HTMLElement, level = 0): any[] {
        const items: any[] = [];

        for (const li of Array.from(element.children)) {
            const childBlocks: any[] = [];

            const liClone = li.cloneNode(true) as HTMLElement;
            Array.from(liClone.querySelectorAll('ul, ol')).forEach((nested) => nested.remove());

            // Extract HTML content of the top-level part of the <li>
            const liHtml = liClone.innerHTML.trim();
            const bulletHtml = `<span>•</span> ${liHtml}`;

            const textBlock = {
                type: 'TextBlock',
                text: bulletHtml,
                wrap: true,
                spacing: 'None', // <<< KEY: reduce space between bullets
            };

            if (liHtml) {
                if (level === 0) {
                    childBlocks.push(textBlock);
                } else {
                    childBlocks.push({
                        type: 'ColumnSet',
                        spacing: 'None', // <<< avoid extra padding around the set
                        columns: [
                            {
                                type: 'Column',
                                width: '20px',
                                items: [{ type: 'TextBlock', text: '', spacing: 'None' }],
                            },
                            {
                                type: 'Column',
                                width: 'stretch',
                                items: [textBlock],
                            },
                        ],
                    });
                }
            }

            const nestedLists = li.querySelectorAll(':scope > ul, :scope > ol');
            nestedLists.forEach((nestedList) => {
                const nestedItems = this.transformList(nestedList as HTMLElement, level + 1);
                childBlocks.push(...nestedItems);
            });

            items.push(...childBlocks);
        }

        return items;
    }

    /**
     *
     * @param element
     */
    private transformText(element: any): any {
        const text = this.parseInlineElements(element);
        return {
            type: 'TextBlock',
            text,
            width: 'stretch',
            wrap: true,
        };
    }

    /**
     *
     * @param element
     */
    private parseInlineElements(element: HTMLElement): string {
        let result = '';

        for (const node of Array.from(element.childNodes)) {
            if (node.nodeType === Node.TEXT_NODE) {
                result += node.textContent;
            }

            if (node.nodeType === Node.ELEMENT_NODE) {
                const el = node as HTMLElement;

                if (el.tagName.toLowerCase() === 'a') {
                    const href = el.getAttribute('href');
                    const text = el.innerText;

                    if (href) {
                        result += `[${text}](${href})`;
                    } else {
                        result += text;
                    }
                } else {
                    result += this.parseInlineElements(el);
                }
            }
        }

        return result.trim();
    }

    /**
     *
     * @param htmlText
     */
    private generateStyledTables(htmlText: string): string[] {
        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlText, 'text/html');
        const tables = Array.from(doc.querySelectorAll('table'));

        if (!tables.length) {
            console.error('No tables found in the provided HTML.');
            return [];
        }

        return tables.map((table) => {
            // Style the table
            table.style.borderRadius = '10px';
            table.style.overflow = 'hidden';

            const headers = table.querySelectorAll('th');
            headers.forEach((th) => {
                th.style.backgroundColor = '#0a1c47';
                th.style.color = 'white';
                th.style.padding = '10px';
                th.style.border = '2px solid #dddddd';
            });

            const cells = table.querySelectorAll('td');
            cells.forEach((td) => {
                td.style.border = '2px solid #dddddd';
                td.style.padding = '10px';
                td.style.color = '#606060';
            });

            const rows = table.querySelectorAll('tr');
            rows.forEach((row) => {
                const firstCell = row.querySelector('td');
                if (firstCell) {
                    firstCell.style.backgroundColor = '#f2f2f2';
                    firstCell.style.fontWeight = 'bold';
                }
            });

            const cleanResult = this.replaceTableCharacters(table.outerHTML);

            return cleanResult;
        });
    }

    /**
     * Replaces newline characters in the prompt result with an empty string.
     * This is used to clean up the result before displaying it in the adaptive card.
     * @param promptResult - The input string containing newline characters.
     * @returns The cleaned-up string with newline characters removed.
     */
    private replaceTableCharacters(promptResult): string {
        if (!promptResult || typeof promptResult !== 'string') {
            return promptResult;
        }
        return promptResult.replace(/\\n/g, '');
    }
}
